import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'biel-header',
  styleUrl: 'biel-header.css',
  shadow: true,
})
export class BielHeader {
  @Prop() headerTitle: string;
  @Prop() project: string;
  @Prop() hideCloseButton: boolean;
  @Prop() hideExpandButton: boolean;
  @Prop() hideRefreshButton: boolean;
  @Prop() hideSettingsButton: boolean = false;
  @Prop() hideTooltips: boolean = false;
  @Prop() showPoweredBy: boolean = true;
  @Prop() fullscreen: boolean;
  @Prop() bielId: string;
  @Prop() thinkModeEnabled: boolean = false;
  @Prop() currentThinkMode: 'auto' | 'fast' | 'think' = 'auto';
  @Prop() refreshButtonText: string = 'Refresh';
  @Prop() expandButtonText: string = 'Expand';
  @Prop() collapseButtonText: string = 'Collapse';
  @Prop() closeButtonText: string = 'Close';
  @Prop() settingsButtonText: string = 'Settings';

  @State() isFullscreen: boolean;
  @State() isThinkModeActive: boolean;
  @State() showThinkModeDropdown: boolean = false;
  @State() selectedThinkMode: 'auto' | 'fast' | 'think' = 'auto';

  @Event() closeClicked: EventEmitter<{ bielId: string }>;
  @Event() fullscreenClicked: EventEmitter<{ bielId: string }>;
  @Event() refreshClicked: EventEmitter<{ bielId: string }>;
  @Event() thinkModeToggled: EventEmitter<{ bielId: string; isActive: boolean }>;
  @Event() thinkModeChanged: EventEmitter<{ bielId: string; mode: 'auto' | 'fast' | 'think' }>;

  componentWillLoad() {
    this.isFullscreen = this.fullscreen;
    this.isThinkModeActive = this.thinkModeEnabled;
    this.selectedThinkMode = this.currentThinkMode;
  }

  @Watch('currentThinkMode')
  handleCurrentThinkModeChange(newValue: 'auto' | 'fast' | 'think') {
    this.selectedThinkMode = newValue;
  }

  handleCloseClick() {
    this.closeClicked.emit({ bielId: this.bielId });
  }

  handleFullscreenClick() {
    this.isFullscreen = !this.isFullscreen;
    this.fullscreenClicked.emit({ bielId: this.bielId });
  }

  handleRefreshClick() {
    this.refreshClicked.emit({ bielId: this.bielId });
  }

  handleThinkModeClick() {
    this.isThinkModeActive = !this.isThinkModeActive;
    this.thinkModeToggled.emit({ bielId: this.bielId, isActive: this.isThinkModeActive });
  }

  handleSettingsClick(event: Event) {
    event.stopPropagation();
    this.showThinkModeDropdown = !this.showThinkModeDropdown;
  }

  handleThinkModeSelect(mode: 'auto' | 'fast' | 'think') {
    this.selectedThinkMode = mode;
    this.showThinkModeDropdown = false;
    this.thinkModeChanged.emit({ bielId: this.bielId, mode });
  }

  handleClickOutside = (event: MouseEvent) => {
    const dropdown = (event.target as HTMLElement).closest('.biel-header__settings-dropdown');
    const button = (event.target as HTMLElement).closest('.biel-header__settings-button');

    if (!dropdown && !button && this.showThinkModeDropdown) {
      this.showThinkModeDropdown = false;
    }
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  render() {
    return (
      <header class="biel-header">
        <div class="biel-header__title-container">
          <div class="biel-header__title">{this.headerTitle}</div>
          <div class="biel-header__powered-by">
            {this.showPoweredBy && (
              <span>
                Powered by{' '}
                <a target="_blank" href="https://biel.ai">
                  biel.ai
                </a>
              </span>
            )}
          </div>
        </div>

        <div class="biel-header__icons">
          {!this.hideSettingsButton && (
            <div class="biel-header__settings-container">
              <button
                class="biel-header__icon biel-header__settings-button"
                onClick={(event) => this.handleSettingsClick(event)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-settings"
                >
                  <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {!this.hideTooltips && <span class="biel-header__tooltip">{this.settingsButtonText}</span>}
              </button>
              {this.showThinkModeDropdown && (
                <div class="biel-header__settings-dropdown">
                  <button
                    class={`biel-header__dropdown-item ${this.selectedThinkMode === 'auto' ? 'biel-header__dropdown-item--selected' : ''}`}
                    onClick={() => this.handleThinkModeSelect('auto')}
                  >
                    <span class="biel-header__dropdown-title">Auto</span>
                    <span class="biel-header__dropdown-description">Balances speed and quality</span>
                  </button>
                  <button
                    class={`biel-header__dropdown-item ${this.selectedThinkMode === 'fast' ? 'biel-header__dropdown-item--selected' : ''}`}
                    onClick={() => this.handleThinkModeSelect('fast')}
                  >
                    <span class="biel-header__dropdown-title">Instant</span>
                    <span class="biel-header__dropdown-description">Prioritizes speed</span>
                  </button>
                  <button
                    class={`biel-header__dropdown-item ${this.selectedThinkMode === 'think' ? 'biel-header__dropdown-item--selected' : ''}`}
                    onClick={() => this.handleThinkModeSelect('think')}
                  >
                    <span class="biel-header__dropdown-title">Think</span>
                    <span class="biel-header__dropdown-description">Takes longer for better accuracy</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {!this.hideRefreshButton && (
            <button
              class="biel-header__icon"
              onClick={() => this.handleRefreshClick()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-refresh-cw"
              >
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M8 16H3v5" />
              </svg>
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.refreshButtonText}</span>}
            </button>
          )}
          {!this.hideExpandButton && (
            <button
              class="biel-header__icon"
              onClick={() => this.handleFullscreenClick()}
            >
              {this.isFullscreen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-minimize-2"
                >
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" x2="21" y1="10" y2="3" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-maximize-2"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" x2="14" y1="3" y2="10" />
                  <line x1="3" x2="10" y1="21" y2="14" />
                </svg>
              )}
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.isFullscreen ? this.collapseButtonText : this.expandButtonText}</span>}
            </button>
          )}
          {!this.hideCloseButton && (
            <button class="biel-header__icon" onClick={() => this.handleCloseClick()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-x"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
              {!this.hideTooltips && <span class="biel-header__tooltip">{this.closeButtonText}</span>}
            </button>
          )}
        </div>
      </header>
    );
  }
}
